import React, { useState, useEffect } from 'react';
import { formatUKDate, formatUKDateTime, formatUKCurrency } from '../../utils/formatters';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const CreateReturn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [returnReasons, setReturnReasons] = useState([]);
  const [returnData, setReturnData] = useState({
    customerEmail: '',
    customerName: '',
    returnReason: '',
    customReasonText: '',
    notes: '',
    refundAmount: 0,
    markAsReceived: false
  });
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [customReasonDialog, setCustomReasonDialog] = useState(false);
  const [newCustomReason, setNewCustomReason] = useState('');
  const [error, setError] = useState('');
  const [sellercloudRmaInfo, setSellercloudRmaInfo] = useState(null);

  const steps = ['Search Order', 'Select Items', 'Return Details', 'Review & Submit'];

  useEffect(() => {
    fetchReturnReasons();
    
    // Check for pre-populated order data from URL params or location state
    const orderId = searchParams.get('orderId');
    const platform = searchParams.get('platform');
    const prePopulatedOrder = location.state?.order;
    const quickData = location.state?.quickData;
    
    if (quickData) {
      // Quick return data passed from ReturnsTable
      setOrderSearch(quickData.orderId);
      setReturnData({
        customerEmail: quickData.customerEmail,
        customerName: quickData.customerName,
        returnReason: quickData.reason,
        customReasonText: '',
        notes: quickData.notes,
        refundAmount: 0
      });
      // Auto-search for the order if order ID is provided
      if (quickData.orderId.trim()) {
        setTimeout(() => {
          searchOrders();
        }, 500);
      }
    } else if (prePopulatedOrder) {
      // Direct order object passed via navigation state
      handlePrePopulatedOrder(prePopulatedOrder);
    } else if (orderId && platform) {
      // Order ID and platform passed via URL parameters
      fetchOrderById(orderId, platform);
    }
  }, [searchParams, location.state]);

  const fetchReturnReasons = async () => {
    try {
      const response = await axios.get('/api/returns/sellercloud-reasons');
      console.log('Fetched return reasons:', response.data.data);
      setReturnReasons(response.data.data || []);
    } catch (error) {
      console.error('Error fetching return reasons:', error);
      setError('Failed to load return reasons. Please refresh the page.');
    }
  };

  const handleAddCustomReason = async () => {
    if (!newCustomReason.trim()) return;
    
    try {
      setLoading(true);
      const reasonCode = newCustomReason.toUpperCase().replace(/[^A-Z0-9]/g, '_');
      
      const response = await axios.post('/api/returns/reasons', {
        reason_code: reasonCode,
        reason_description: newCustomReason.trim(),
        category: 'Custom'
      });
      
      if (response.data.success) {
        // Add the new reason to the local state
        const newReason = response.data.data;
        setReturnReasons(prev => [...prev, newReason]);
        
        // Set it as the selected reason
        setReturnData(prev => ({...prev, returnReason: newReason.reason_code}));
        
        // Close dialog and reset
        setCustomReasonDialog(false);
        setNewCustomReason('');
      }
    } catch (error) {
      console.error('Error adding custom reason:', error);
      setError('Failed to add custom reason. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const searchOrders = async () => {
    if (!orderSearch.trim()) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Use optimized fast search endpoint for better performance
      const response = await axios.get(`/api/orders/search-fast?query=${encodeURIComponent(orderSearch.trim())}`);
      
      if (response.data.success) {
        const sellercloudsResults = response.data.data.sellercloud || [];
        const amazonResults = response.data.data.amazon || [];
        
        // Show all results but prioritize Sellercloud for returns
        const allResults = [...sellercloudsResults];
        
        // Add Amazon results if found (for cross-reference info)
        if (amazonResults.length > 0 && sellercloudsResults.length === 0) {
          // If no Sellercloud results, show Amazon results but with warning
          setSearchResults(amazonResults.map(result => ({ ...result, isAmazonOnly: true })));
          setError('Found Amazon order but no matching Sellercloud order. Amazon-only orders cannot be processed for returns through this system.');
        } else {
          setSearchResults(allResults);
          
          if (allResults.length === 0) {
            setError('No orders found. Please check the order number and try again.');
          }
        }
        
        // Log performance info
        if (response.data.performance) {
          console.log(`🚀 Search completed in ${response.data.performance.searchTime}ms (cached: ${response.data.performance.cached})`);
        }
      } else {
        setError(response.data.error || 'Failed to search orders');
      }
    } catch (error) {
      console.error('Order search error:', error);
      setError('Failed to search orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateExistingRMA = (order) => {
    // Set the order and navigate to update RMA mode
    setSelectedOrder(order);
    setReturnData({
      ...returnData,
      customerEmail: order.customerEmail || '',
      customerName: order.customerName || `Customer (${order.customerEmail?.split('@')[0] || 'Unknown'})`,
      isUpdatingExisting: true,
      existingRmaId: order.rmaInfo.rmaId,
      existingRmaStatus: order.rmaInfo.rmaStatus
    });
    setActiveStep(2); // Skip item selection and go to return details step
  };

  const selectOrder = async (order) => {
    setSelectedOrder(order);
    
    // Try to get better customer information, especially for Amazon orders
    let customerEmail = order.customerEmail || order.BuyerInfo?.BuyerEmail || '';
    let customerName = order.customerName || order.BuyerInfo?.BuyerName || '';
    
    // For Amazon orders, try to get real customer name from Sellercloud if available
    if (order.platform === 'amazon' && order.SellerOrderId) {
      try {
        const sellercloudResponse = await axios.get(`/api/orders/sellercloud/${order.SellerOrderId}`);
        if (sellercloudResponse.data.success && sellercloudResponse.data.data.order) {
          const scOrder = sellercloudResponse.data.data.order;
          if (scOrder.OrderDetails) {
            customerEmail = scOrder.OrderDetails.CustomerEmail || customerEmail;
            customerName = scOrder.OrderDetails.CustomerName || 
                          `${scOrder.OrderDetails.FirstName || ''} ${scOrder.OrderDetails.LastName || ''}`.trim() ||
                          customerName;
          }
          
          // Try to get customer name from ShippingAddress if not found in OrderDetails
          if ((!customerName || customerName === '') && scOrder.ShippingAddress) {
            const shipping = scOrder.ShippingAddress;
            customerName = `${shipping.FirstName || ''} ${shipping.LastName || ''}`.trim();
          }
        }
      } catch (error) {
        console.log('Could not fetch Sellercloud customer data:', error.message);
      }
    }
    
    setReturnData({
      ...returnData,
      customerEmail: customerEmail,
      customerName: customerName || `Customer (${customerEmail.split('@')[0]})`
    });
    
    // Get order items - for Amazon they should be included, for others fetch separately
    let orderWithItems = { ...order };
    
    try {
      if (order.platform === 'amazon') {
        // Items might already be included, or fetch them from the detailed endpoint
        if (!order.items) {
          const orderResponse = await axios.get(`/api/orders/amazon/${order.orderId}`);
          if (orderResponse.data.success && orderResponse.data.data.items) {
            // Map Amazon item structure to our standard format
            orderWithItems.items = orderResponse.data.data.items.map(item => ({
              sku: item.SellerSKU,
              productName: item.Title,
              title: item.Title,
              quantity: item.QuantityOrdered,
              unitPrice: parseFloat(item.ItemPrice?.Amount || 0),
              sellerSku: item.SellerSKU,
              asin: item.ASIN,
              orderItemId: item.OrderItemId
            }));
          }
        }
      } else {
        // For other platforms, fetch items separately if not included
        if (!order.items) {
          let itemsResponse;
          switch (order.platform) {
            case 'sellercloud':
              itemsResponse = await axios.get(`/api/sellercloud/orders/${order.orderId}/items`);
              break;
            case 'ebay':
              itemsResponse = await axios.get(`/api/orders/ebay/${order.orderId}/items`);
              break;
          }
          
          if (itemsResponse?.data.success && itemsResponse.data.data) {
            orderWithItems.items = itemsResponse.data.data;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching order items:', error);
    }
    
    setSelectedOrder(orderWithItems);
    setActiveStep(1);
  };

  const fetchOrderById = async (orderId, platform) => {
    setLoading(true);
    try {
      let orderResponse;
      switch (platform.toLowerCase()) {
        case 'amazon':
          orderResponse = await axios.get(`/api/amazon/orders/${orderId}`);
          break;
        case 'sellercloud':
          orderResponse = await axios.get(`/api/sellercloud/orders/${orderId}`);
          break;
        case 'ebay':
          orderResponse = await axios.get(`/api/ebay/orders/${orderId}`);
          break;
        default:
          throw new Error('Invalid platform');
      }
      
      if (orderResponse.data.data) {
        const order = { ...orderResponse.data.data, platform: platform.toLowerCase() };
        handlePrePopulatedOrder(order);
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      setError(`Failed to load order ${orderId} from ${platform}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePrePopulatedOrder = async (order) => {
    setSelectedOrder(order);
    
    // Try to get better customer information, especially for Amazon orders
    let customerEmail = order.customerEmail || order.BuyerInfo?.BuyerEmail || '';
    let customerName = order.customerName || order.BuyerInfo?.BuyerName || '';
    
    // For Amazon orders, try to get real customer name from Sellercloud if available
    if (order.platform === 'amazon' && order.SellerOrderId) {
      try {
        const sellercloudResponse = await axios.get(`/api/orders/sellercloud/${order.SellerOrderId}`);
        if (sellercloudResponse.data.success && sellercloudResponse.data.data.order) {
          const scOrder = sellercloudResponse.data.data.order;
          if (scOrder.OrderDetails) {
            customerEmail = scOrder.OrderDetails.CustomerEmail || customerEmail;
            customerName = scOrder.OrderDetails.CustomerName || 
                          `${scOrder.OrderDetails.FirstName || ''} ${scOrder.OrderDetails.LastName || ''}`.trim() ||
                          customerName;
          }
          
          // Try to get customer name from ShippingAddress if not found in OrderDetails
          if ((!customerName || customerName === '') && scOrder.ShippingAddress) {
            const shipping = scOrder.ShippingAddress;
            customerName = `${shipping.FirstName || ''} ${shipping.LastName || ''}`.trim();
          }
        }
      } catch (error) {
        console.log('Could not fetch Sellercloud customer data:', error.message);
      }
    }
    
    setReturnData({
      ...returnData,
      customerEmail: customerEmail,
      customerName: customerName || `Customer (${customerEmail.split('@')[0]})`
    });
    
    // Get order items - for Amazon they should be included, for others fetch separately
    let orderWithItems = { ...order };
    
    try {
      if (order.platform === 'amazon') {
        // Items might already be included, or fetch them from the detailed endpoint
        if (!order.items) {
          const orderResponse = await axios.get(`/api/orders/amazon/${order.orderId}`);
          if (orderResponse.data.success && orderResponse.data.data.items) {
            // Map Amazon item structure to our standard format
            orderWithItems.items = orderResponse.data.data.items.map(item => ({
              sku: item.SellerSKU,
              productName: item.Title,
              title: item.Title,
              quantity: item.QuantityOrdered,
              unitPrice: parseFloat(item.ItemPrice?.Amount || 0),
              sellerSku: item.SellerSKU,
              asin: item.ASIN,
              orderItemId: item.OrderItemId
            }));
          }
        }
      } else {
        // For other platforms, fetch items separately if not included
        if (!order.items) {
          let itemsResponse;
          switch (order.platform) {
            case 'sellercloud':
              itemsResponse = await axios.get(`/api/sellercloud/orders/${order.orderId}/items`);
              break;
            case 'ebay':
              itemsResponse = await axios.get(`/api/orders/ebay/${order.orderId}/items`);
              break;
          }
          
          if (itemsResponse?.data.success && itemsResponse.data.data) {
            orderWithItems.items = itemsResponse.data.data;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching order items:', error);
    }
    
    setSelectedOrder(orderWithItems);
    setActiveStep(1);
  };



  const toggleItemSelection = (item) => {
    const isSelected = selectedItems.find(selected => selected.sku === item.sku);
    if (isSelected) {
      setSelectedItems(selectedItems.filter(selected => selected.sku !== item.sku));
    } else {
      setSelectedItems([...selectedItems, { ...item, returnQuantity: 1 }]);
    }
  };

  const updateReturnQuantity = (sku, quantity) => {
    setSelectedItems(selectedItems.map(item => 
      item.sku === sku ? { ...item, returnQuantity: quantity } : item
    ));
  };

  const calculateRefundAmount = () => {
    return selectedItems.reduce((total, item) => 
      total + ((item.unitPrice || item.price) * item.returnQuantity), 0);
  };

  const submitReturn = async () => {
    console.log('submitReturn called', { returnData, selectedOrder, selectedItems });
    setLoading(true);
    setError(''); // Clear previous errors
    try {
      if (returnData.isUpdatingExisting) {
        // Update existing RMA
        const updatePayload = {
          rmaId: returnData.existingRmaId,
          orderId: selectedOrder.orderId,
          platform: selectedOrder.platform,
          notes: returnData.notes,
          markAsReceived: returnData.markAsReceived === true
        };

        const response = await axios.put(`/api/returns/rma/${returnData.existingRmaId}`, updatePayload);
        
        if (response.data.success) {
          console.log('RMA update response:', response.data);
          setSellercloudRmaInfo({
            success: true,
            rmaId: returnData.existingRmaId,
            message: 'RMA updated successfully',
            action: 'updated'
          });
          setActiveStep(3);
          setTimeout(() => {
            navigate('/returns');
          }, 3000);
        }
      } else {
        // Create new RMA with both Amazon and Sellercloud IDs when available
        const returnPayload = {
          customer_email: returnData.customerEmail,
          customer_name: returnData.customerName,
          return_reason: returnData.returnReason,
          notes: returnData.notes,
          refund_amount: calculateRefundAmount(),
          items_returned: JSON.stringify(selectedItems),
          markAsReceived: returnData.markAsReceived
        };

        // Add order IDs based on what's available
        if (selectedOrder.platform === 'amazon') {
          returnPayload.amazon_order_id = selectedOrder.orderId;
          // If Amazon order has a linked Sellercloud order, include that too
          if (selectedOrder.SellerOrderId || selectedOrder.sellercloudOrderId) {
            returnPayload.sellercloud_order_id = selectedOrder.SellerOrderId || selectedOrder.sellercloudOrderId;
            console.log(`🔗 Including linked Sellercloud order ID: ${returnPayload.sellercloud_order_id} for Amazon order: ${returnPayload.amazon_order_id}`);
          } else {
            console.log(`❌ NO SELLERCLOUD ID FOUND: selectedOrder.SellerOrderId=${selectedOrder.SellerOrderId}, selectedOrder.sellercloudOrderId=${selectedOrder.sellercloudOrderId}`);
            console.log('Full selectedOrder object:', selectedOrder);
          }
        } else if (selectedOrder.platform === 'sellercloud') {
          returnPayload.sellercloud_order_id = selectedOrder.orderId;
          // If Sellercloud order has a linked channel order, include that too
          if (selectedOrder.channelOrderId || selectedOrder.OrderSourceOrderID) {
            returnPayload.amazon_order_id = selectedOrder.channelOrderId || selectedOrder.OrderSourceOrderID;
            console.log(`🔗 Including linked Amazon order ID: ${returnPayload.amazon_order_id} for Sellercloud order: ${returnPayload.sellercloud_order_id}`);
          } else {
            console.log(`❌ NO AMAZON ORDER ID FOUND for Sellercloud order: selectedOrder.channelOrderId=${selectedOrder.channelOrderId}, selectedOrder.OrderSourceOrderID=${selectedOrder.OrderSourceOrderID}`);
            console.log('Full selectedOrder object:', selectedOrder);
          }
        } else if (selectedOrder.platform === 'ebay') {
          returnPayload.ebay_order_id = selectedOrder.orderId;
        }

        console.log('Return payload with linked order IDs:', returnPayload);

        const response = await axios.post('/api/returns', returnPayload);
        
        if (response.data.success) {
          console.log('Return creation response:', response.data);
          
          // Check if Sellercloud RMA was created successfully
          const sellercloudResult = response.data.sellercloudIntegration;
          setSellercloudRmaInfo(sellercloudResult);
          
          if (sellercloudResult && sellercloudResult.success) {
            console.log(`✅ Sellercloud RMA ${sellercloudResult.rmaId} created successfully`);
          } else if (sellercloudResult && !sellercloudResult.success) {
            console.warn('⚠️ Sellercloud RMA creation failed:', sellercloudResult.message);
          }
          
          setActiveStep(3);
          if (sellercloudResult?.success) {
            setTimeout(() => navigate('/returns'), 3000);
          }
        }
      }
    } catch (error) {
      console.error('Error creating return:', error);
      setError('Failed to create return. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const getPlatformColor = (platform) => {
    const colors = {
      amazon: 'primary',
      sellercloud: 'secondary',
      ebay: 'warning'
    };
    return colors[platform] || 'default';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <img 
          src="https://www.xs-stock.co.uk/cdn/shop/files/XS_NEW_LOGO_2ac57727-dd78-4de8-bd11-0c0bb84a6858.png?v=1738612353&width=400" 
          alt="XS Stock"
          style={{
            height: '32px',
            marginRight: '12px',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
          }}
        />
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A365D' }}>
          Create New Return
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{}} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {selectedOrder && activeStep > 0 && (
            <Alert severity="info" sx={{}}>
              Creating return for Order #{selectedOrder.orderId || selectedOrder.id} from {selectedOrder.platform?.toUpperCase()}
            </Alert>
          )}

          {/* Step 0: Search Order */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Search for Sellercloud Order
              </Typography>
              <Box sx={{ display: 'flex', mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Enter Sellercloud order ID, OrderSourceOrderId (channel order ID), Channel Ref, email, or customer name..."
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchOrders()}
                  onPaste={(e) => {
                    // Wait for the paste to complete, then search
                    setTimeout(() => {
                      const pastedValue = e.target.value;
                      if (pastedValue.trim()) {
                        console.log('🔍 Auto-searching after paste:', pastedValue);
                        searchOrders();
                      }
                    }, 100); // Small delay to ensure paste content is in the input
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button 
                  variant="contained" 
                  onClick={searchOrders}
                  disabled={loading}
                  sx={{ minWidth: '120px' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Search'}
                </Button>
              </Box>

              {loading && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  py: 6,
                  bgcolor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      Searching orders...
                    </Typography>
                  </Box>
                </Box>
              )}

              {!loading && searchResults.length === 0 && orderSearch && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  py: 6,
                  bgcolor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <SearchIcon sx={{ fontSize: 48, color: '#6c757d', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Ready to search for orders
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Click the Search button above to find Sellercloud orders by ID, email, or customer name
                    </Typography>
                  </Box>
                </Box>
              )}

              {!loading && searchResults.length > 0 && (
                <>
                  <Box sx={{ 
                    mb: 2, 
                    p: 2, 
                    bgcolor: '#e8f5e8', 
                    borderRadius: '6px',
                    border: '1px solid #c8e6c9'
                  }}>
                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                      🔍 Found {searchResults.length} Sellercloud order{searchResults.length !== 1 ? 's' : ''} matching "{orderSearch}"
                    </Typography>
                  </Box>
                <TableContainer sx={{ 
                  px: 0, 
                  width: '100%', 
                  maxHeight: 'calc(100vh - 400px)', 
                  overflow: 'auto',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px'
                }}>
                  <Table stickyHeader size="small" sx={{ width: '100%' }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          py: 1.5, 
                          px: 2, 
                          fontSize: '0.875rem', 
                          color: '#495057', 
                          borderBottom: '2px solid #dee2e6' 
                        }}>
                          Order ID
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          py: 1.5, 
                          px: 2, 
                          fontSize: '0.875rem', 
                          color: '#495057', 
                          borderBottom: '2px solid #dee2e6' 
                        }}>
                          OrderSourceOrderId
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          py: 1.5, 
                          px: 2, 
                          fontSize: '0.875rem', 
                          color: '#495057', 
                          borderBottom: '2px solid #dee2e6' 
                        }}>
                          Platform
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          py: 1.5, 
                          px: 2, 
                          fontSize: '0.875rem', 
                          color: '#495057', 
                          borderBottom: '2px solid #dee2e6' 
                        }}>
                          Customer
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          py: 1.5, 
                          px: 2, 
                          fontSize: '0.875rem', 
                          color: '#495057', 
                          borderBottom: '2px solid #dee2e6' 
                        }}>
                          Date
                        </TableCell>
                        <TableCell sx={{ 
                          fontWeight: 600, 
                          py: 1.5, 
                          px: 2, 
                          fontSize: '0.875rem', 
                          color: '#495057', 
                          borderBottom: '2px solid #dee2e6' 
                        }}>
                          Total
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          fontWeight: 600, 
                          py: 1.5, 
                          px: 2, 
                          fontSize: '0.875rem', 
                          color: '#495057', 
                          borderBottom: '2px solid #dee2e6' 
                        }}>
                          RMA Status
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          fontWeight: 600, 
                          py: 1.5, 
                          px: 2, 
                          fontSize: '0.875rem', 
                          color: '#495057', 
                          borderBottom: '2px solid #dee2e6' 
                        }}>
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchResults.map((order, index) => (
                        <TableRow 
                          key={index} 
                          hover
                          sx={{ 
                            '&:hover': { bgcolor: '#f8f9fa' },
                            borderBottom: '1px solid #e9ecef',
                            bgcolor: index % 2 === 0 ? '#ffffff' : '#fafbfc'
                          }}
                        >
                          <TableCell sx={{ 
                            py: 1.5, 
                            px: 2, 
                            fontSize: '0.875rem', 
                            fontWeight: 500, 
                            color: '#495057' 
                          }}>
                            {order.orderId || order.id}
                          </TableCell>
                          <TableCell sx={{ 
                            py: 1.5, 
                            px: 2, 
                            fontSize: '0.875rem', 
                            color: '#495057' 
                          }}>
                            {order.channelOrderId || order.OrderSourceOrderID || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ py: 1.5, px: 2 }}>
                            <Chip 
                              label={order.platform.toUpperCase()} 
                              size="small"
                              sx={{ 
                                height: '18px', 
                                fontSize: '0.65rem',
                                fontWeight: 500,
                                bgcolor: order.platform === 'sellercloud' ? '#e7f3ff' : 
                                         order.platform === 'ebay' ? '#fff2e6' : 
                                         order.platform === 'amazon' ? '#fff3cd' : '#f0f0f0',
                                color: order.platform === 'sellercloud' ? '#0066cc' : 
                                       order.platform === 'ebay' ? '#cc6600' : 
                                       order.platform === 'amazon' ? '#856404' : '#666666'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1.5, px: 2 }}>
                            <Box>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.875rem', 
                                fontWeight: 500, 
                                color: '#212529', 
                                mb: 0.5 
                              }}>
                                {order.customerName || order.buyerName || 'N/A'}
                              </Typography>
                              <Typography variant="caption" sx={{ 
                                color: '#6c757d', 
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                maxWidth: '200px'
                              }}>
                                {order.customerEmail || order.buyerEmail || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ 
                            py: 1.5, 
                            px: 2, 
                            fontSize: '0.875rem', 
                            color: '#495057' 
                          }}>
                            {order.orderDate || order.createdDate ? 
                              formatUKDate(order.orderDate || order.createdDate) : 
                              'Invalid Date'
                            }
                          </TableCell>
                          <TableCell sx={{ 
                            py: 1.5, 
                            px: 2, 
                            fontSize: '0.875rem', 
                            fontWeight: 600, 
                            color: '#212529' 
                          }}>
                            {formatCurrency(order.total || order.totalAmount)}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1.5, px: 2 }}>
                            {order.rmaInfo && order.rmaInfo.exists ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                <Chip 
                                  label={`RMA #${order.rmaInfo.rmaId}`}
                                  size="small"
                                  sx={{ 
                                    height: '20px', 
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    bgcolor: order.rmaInfo.isReceived ? '#d1ecf1' : '#fff3cd',
                                    color: order.rmaInfo.isReceived ? '#0c5460' : '#856404',
                                    border: order.rmaInfo.isReceived ? '1px solid #bee5eb' : '1px solid #ffeaa7'
                                  }}
                                />
                                {order.rmaInfo.isReceived && (
                                  <Chip 
                                    label="RECEIVED"
                                    size="small"
                                    sx={{ 
                                      height: '16px', 
                                      fontSize: '0.6rem',
                                      fontWeight: 600,
                                      bgcolor: '#28a745',
                                      color: 'white',
                                      '& .MuiChip-label': {
                                        px: 1
                                      }
                                    }}
                                  />
                                )}
                                {order.rmaInfo.rmaStatusText && (
                                  <Typography variant="caption" sx={{ 
                                    fontSize: '0.6rem',
                                    color: '#6c757d',
                                    lineHeight: 1
                                  }}>
                                    {order.rmaInfo.rmaStatusText}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Chip 
                                label="No RMA"
                                size="small"
                                sx={{ 
                                  height: '20px', 
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                  bgcolor: '#d4edda',
                                  color: '#155724',
                                  border: '1px solid #c3e6cb'
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1.5, px: 2 }}>
                            {order.rmaInfo && order.rmaInfo.exists ? (
                              <Button 
                                variant="contained"
                                size="small"
                                onClick={() => handleUpdateExistingRMA(order)}
                                sx={{ 
                                  minWidth: '100px',
                                  height: '32px',
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  bgcolor: '#28a745',
                                  '&:hover': {
                                    bgcolor: '#218838'
                                  }
                                }}
                              >
                                Update RMA
                              </Button>
                            ) : (
                              <Button 
                                variant="contained"
                                size="small"
                                onClick={() => selectOrder(order)}
                                sx={{ 
                                  minWidth: '80px',
                                  height: '32px',
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  bgcolor: '#0066cc',
                                  '&:hover': {
                                    bgcolor: '#0056b3'
                                  }
                                }}
                              >
                                Select
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Results Summary */}
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  bgcolor: '#f8f9fa', 
                  borderRadius: '6px',
                  border: '1px solid #e9ecef',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {searchResults.length} Sellercloud result{searchResults.length !== 1 ? 's' : ''}
                  </Typography>
                  <Box sx={{ display: 'flex' }}>
                    <Chip 
                      label={`SELLERCLOUD: ${searchResults.length}`}
                      size="small"
                      sx={{ 
                        height: '20px', 
                        fontSize: '0.7rem',
                        bgcolor: '#e7f3ff',
                        color: '#0066cc'
                      }}
                    />
                  </Box>
                </Box>
                </>
              )}
            </Box>
          )}

          {/* Step 1: Select Items */}
          {activeStep === 1 && selectedOrder && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Select Items to Return
              </Typography>
              <Card variant="outlined" sx={{ mb: 3, bgcolor: '#F7FAFC' }}>
                <CardContent sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Order #{selectedOrder.orderId}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedOrder.customerName} • {selectedOrder.customerEmail}
                      </Typography>
                    </Box>
                    <Chip 
                      label={selectedOrder.platform.toUpperCase()} 
                      color={getPlatformColor(selectedOrder.platform)}
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>

              {selectedOrder.items && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">Select</TableCell>
                        <TableCell>Item</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell>Quantity Ordered</TableCell>
                        <TableCell>Unit Price</TableCell>
                        <TableCell>Return Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.items.map((item, index) => {
                        const isSelected = selectedItems.find(selected => selected.sku === item.sku);
                        return (
                          <TableRow key={index} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={!!isSelected}
                                onChange={() => toggleItemSelection(item)}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.productName || item.title || item.name}
                              </Typography>
                            </TableCell>
                            <TableCell>{item.sku || item.sellerSku}</TableCell>
                            <TableCell>{item.quantity}</TableCell>
                            <TableCell>{formatCurrency(item.unitPrice || item.price)}</TableCell>
                            <TableCell>
                              {isSelected && (
                                <TextField
                                  type="number"
                                  size="small"
                                  value={isSelected.returnQuantity}
                                  onChange={(e) => updateReturnQuantity(item.sku, parseInt(e.target.value))}
                                  inputProps={{ min: 1, max: item.quantity }}
                                  sx={{ width: '80px' }}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button onClick={() => setActiveStep(0)}>Back</Button>
                <Button 
                  variant="contained" 
                  onClick={() => setActiveStep(2)}
                  disabled={selectedItems.length === 0}
                >
                  Next ({selectedItems.length} items selected)
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 2: Return Details */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                {returnData.isUpdatingExisting ? `Update RMA #${returnData.existingRmaId}` : 'Return Details'}
              </Typography>
              
              {returnData.isUpdatingExisting && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#856404', mb: 1 }}>
                    Existing RMA Information
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#856404', mb: 1 }}>
                    RMA ID: #{returnData.existingRmaId}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#856404', mb: 1 }}>
                    Order: {selectedOrder?.orderId}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#856404' }}>
                    You can add notes and mark this RMA as received without creating a new RMA number.
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', mb: 3 }}>
                <TextField
                  label="Customer Name"
                  value={returnData.customerName}
                  onChange={(e) => setReturnData({...returnData, customerName: e.target.value})}
                  fullWidth
                  disabled={returnData.isUpdatingExisting}
                />
                <TextField
                  label="Customer Email"
                  value={returnData.customerEmail}
                  onChange={(e) => setReturnData({...returnData, customerEmail: e.target.value})}
                  fullWidth
                  disabled={returnData.isUpdatingExisting}
                />
              </Box>

              {!returnData.isUpdatingExisting && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Return Reason</InputLabel>
                  <Select
                    value={returnData.returnReason}
                    onChange={(e) => setReturnData({...returnData, returnReason: e.target.value})}
                    label="Return Reason"
                  >
                    {returnReasons.map((reason) => (
                      <MenuItem key={reason.code} value={reason.code}>
                        {reason.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                label={returnData.isUpdatingExisting ? "Notes for RMA Update" : "Notes (Optional)"}
                multiline
                rows={4}
                value={returnData.notes}
                onChange={(e) => setReturnData({...returnData, notes: e.target.value})}
                fullWidth
                sx={{ mb: 2 }}
                placeholder={returnData.isUpdatingExisting 
                  ? "Add notes about the return status, condition, or other details..." 
                  : "Optional notes about this return..."}
              />

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={returnData.markAsReceived}
                      onChange={(e) => setReturnData({...returnData, markAsReceived: e.target.checked})}
                    />
                  }
                  label={returnData.isUpdatingExisting 
                    ? "Mark RMA as Received (will add received status note to RMA)"
                    : "Mark as Received (RMA will be created with Receiving Status as Received and Status as Processing)"}
                />
              </FormControl>

              <Card variant="outlined" sx={{}}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Return Summary</Typography>
                  <Typography variant="body1">
                    Items to return: <strong>{selectedItems.length}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Estimated refund: <strong>{formatCurrency(calculateRefundAmount())}</strong>
                  </Typography>
                  {returnData.returnReason && (
                    <Typography variant="body1">
                      Return reason: <strong>
                        {returnReasons.find(r => r.code === returnData.returnReason)?.label || returnData.returnReason}
                      </strong>
                    </Typography>
                  )}
                  
                  {/* Show item details */}
                  {selectedItems.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Items:
                      </Typography>
                      {selectedItems.map((item, index) => (
                        <Box key={index} sx={{ ml: 2, mb: 1 }}>
                          <Typography variant="body2">
                            <strong>SKU:</strong> {item.sku || item.sellerSku || 'N/A'} | <strong>Name:</strong> {item.productName || item.title || item.name || 'N/A'} | <strong>Qty:</strong> {item.returnQuantity}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={() => setActiveStep(1)}>Back</Button>
                <Button 
                  variant="contained" 
                  onClick={() => setConfirmDialog(true)}
                  disabled={returnData.isUpdatingExisting ? !returnData.notes : !returnData.returnReason}
                >
                  {returnData.isUpdatingExisting ? 'Update RMA' : 'Create Return'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 3: Success */}
          {activeStep === 3 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckIcon sx={{ fontSize: 64, color: sellercloudRmaInfo?.success === false ? 'warning.main' : 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                {sellercloudRmaInfo?.success === false ? 'Return Saved — Sync Needs Attention' : returnData.isUpdatingExisting ? 'RMA Updated Successfully!' : 'Return Created Successfully!'}
              </Typography>
              
              {/* Show Sellercloud RMA integration status */}
              {sellercloudRmaInfo && (
                <Box sx={{ mt: 3, mb: 2 }}>
                  {sellercloudRmaInfo.success ? (
                    <Alert severity="success" sx={{ maxWidth: 500, mx: 'auto' }}>
                      <Typography variant="body2">
                        ✅ <strong>
                          {sellercloudRmaInfo.action === 'updated' 
                            ? `Sellercloud RMA #${sellercloudRmaInfo.rmaId} updated successfully!`
                            : `Sellercloud RMA #${sellercloudRmaInfo.rmaId} created successfully!`
                          }
                        </strong>
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        {sellercloudRmaInfo.action === 'updated'
                          ? 'The RMA has been updated with your notes and marked as received.'
                          : 'Your return has been automatically registered in Sellercloud.'
                        }
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ maxWidth: 500, mx: 'auto' }}>
                      <Typography variant="body2">
                        ⚠️ <strong>Return created locally, but Sellercloud RMA creation failed</strong>
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        {sellercloudRmaInfo.message || 'Please create the RMA manually in Sellercloud'}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}
              
              {sellercloudRmaInfo?.success !== false && <Typography variant="body1" color="text.secondary">Redirecting to returns list...</Typography>}
              {sellercloudRmaInfo?.success === false && <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/returns')}>View saved return</Button>}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Custom Reason Dialog */}
      <Dialog
        open={customReasonDialog}
        onClose={() => setCustomReasonDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Custom Return Reason</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a custom return reason. This will be saved for future use.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Custom Reason"
            fullWidth
            variant="outlined"
            value={newCustomReason}
            onChange={(e) => setNewCustomReason(e.target.value)}
            placeholder="e.g., Product not compatible with device"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCustomReasonDialog(false);
            setNewCustomReason('');
          }} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleAddCustomReason}
            variant="contained"
            disabled={!newCustomReason.trim() || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {loading ? 'Adding...' : 'Add Reason'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>
          {returnData.isUpdatingExisting ? `Confirm RMA #${returnData.existingRmaId} Update` : 'Confirm Return Creation'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {returnData.isUpdatingExisting 
              ? `Are you sure you want to update RMA #${returnData.existingRmaId}${returnData.markAsReceived ? ' and mark it as received' : ''}?`
              : `Are you sure you want to create this return for ${formatCurrency(calculateRefundAmount())}?`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setConfirmDialog(false);
              submitReturn();
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateReturn;
